import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState } from "../../components/UI/States";

export default function MaterialCalc() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [annotatedPdf, setAnnotatedPdf] = useState(null);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setMaterials([]);
    setAnnotatedPdf(null);
    setError("");
  };

  const handleSubmit = async () => {
    if (!file) return setError("Please select a blueprint to upload.");

    setLoading(true);
    setError("");
    setMaterials([]);
    setAnnotatedPdf(null);

    try {
      const formData = new FormData();
      formData.append("blueprint", file);

      // No Content-Type header: the browser sets multipart + boundary itself.
      const res = await fetch("/api/sbcf/getmaterials", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setMaterials(data.materials || []);
      setAnnotatedPdf(data.annotated_pdf || null);
    } catch (err) {
      console.error(err);
      setError("Failed to analyze blueprint. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="lg">
        <PageHeader
          icon={AutoAwesomeIcon}
          tone="emerald"
          eyebrow="Sales & Leads"
          title="Blueprint Material Takeoff"
          description="Upload a blueprint (PDF or image) to get an estimated material list."
        />

        <div className="space-y-6">
          <div className="app-card rounded-2xl border p-5">
            <label
              htmlFor="material-blueprint"
              className="mb-2 block text-sm font-semibold"
            >
              Blueprint
            </label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                id="material-blueprint"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileChange}
                className="app-input w-full min-w-0 flex-1 rounded-xl border p-1.5 text-sm file:mr-3 file:cursor-pointer file:rounded-full file:border-0 file:bg-sky-500/15 file:px-4 file:py-1.5 file:text-sm file:font-semibold file:text-sky-700 dark:file:text-sky-300"
              />

              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
                sx={{ minWidth: 180, minHeight: 40 }}
              >
                {loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Analyze Blueprint"
                )}
              </Button>
            </div>
            <p className="app-muted mt-2 text-xs">PDF, PNG or JPG.</p>

            {error && (
              <p className="mt-4 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
                {error}
              </p>
            )}
          </div>

          {materials.length > 0 && (
            <div className="app-card rounded-2xl border p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold tracking-tight">
                  Material List
                </h2>
                {annotatedPdf && (
                  <Button
                    variant="outlined"
                    href={annotatedPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Annotated Blueprint
                  </Button>
                )}
              </div>
              <div className="app-divider overflow-x-auto rounded-xl border">
                <Table size="small" className="min-w-full">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Confidence</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materials.map((m, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{m.category}</TableCell>
                        <TableCell className="font-medium">{m.item}</TableCell>
                        <TableCell className="tabular-nums">{m.quantity}</TableCell>
                        <TableCell>{m.unit}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-full overflow-hidden rounded-full bg-[color:var(--app-hover)]">
                              <div
                                className={`h-2 rounded-full ${
                                  m.confidence > 85
                                    ? "bg-green-500"
                                    : m.confidence > 60
                                    ? "bg-yellow-500"
                                    : "bg-red-500"
                                }`}
                                style={{ width: `${m.confidence}%` }}
                              ></div>
                            </div>
                            <span className="app-muted text-xs tabular-nums">
                              {m.confidence}%
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* No list returned, but the server still sent an annotated copy. */}
          {annotatedPdf && materials.length === 0 && (
            <div>
              <Button
                variant="outlined"
                href={annotatedPdf}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Annotated Blueprint
              </Button>
            </div>
          )}

          {!loading && materials.length === 0 && !error && (
            <div className="app-card rounded-2xl border">
              <EmptyState
                compact
                icon={UploadFileIcon}
                title="No blueprint analyzed yet"
                description="Upload a blueprint to begin analysis."
              />
            </div>
          )}
        </div>
      </PageShell>
    </div>
  );
}
